/**
 * FreshnessBadge — J7-W4 task 6a (ART-02)
 *
 * Renders a green / yellow / red pill based on a `reviewDueAt` ISO date and
 * today's date. Used by KnowledgeItemsPage (per-row column) and
 * KnowledgeDetailPage (header banner). Extracted as a small component so the
 * 3-tier color logic is consistent across both surfaces (rule #3 — reuse
 * before duplication).
 *
 *   reviewDueAt = null          → "Never reviewed" (yellow)
 *   reviewDueAt < now           → "Overdue"        (red)
 *   reviewDueAt - now < 14d     → "Due soon"       (yellow)
 *   else                        → "Up to date"     (green)
 */

import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { Clock, AlertTriangle, CircleCheck } from 'lucide-react';
import { t } from '@/lib/i18n';

const DUE_SOON_DAYS = 14;

export type FreshnessState = 'overdue' | 'due_soon' | 'fresh' | 'never';

export function computeFreshnessState(reviewDueAt: string | null | undefined): FreshnessState {
  if (!reviewDueAt) return 'never';
  const due = new Date(reviewDueAt).getTime();
  const now = Date.now();
  const diffMs = due - now;
  if (diffMs <= 0) return 'overdue';
  const diffDays = diffMs / (24 * 60 * 60 * 1000);
  if (diffDays <= DUE_SOON_DAYS) return 'due_soon';
  return 'fresh';
}

export interface FreshnessBadgeProps {
  reviewDueAt: string | null | undefined;
  /** When true, render the icon + text pill. When false (default), text-only pill. */
  withIcon?: boolean;
  /** Extra Tailwind classes for the wrapping <Badge>. */
  className?: string;
}

export function FreshnessBadge({ reviewDueAt, withIcon = true, className }: FreshnessBadgeProps) {
  const state = computeFreshnessState(reviewDueAt);
  switch (state) {
    case 'overdue':
      return (
        <Badge variant="error" className={className} data-testid="freshness-badge-overdue">
          {withIcon && <AlertTriangle className="h-3 w-3 mr-1" aria-hidden />}
          {t('Overdue')}
        </Badge>
      );
    case 'due_soon':
      return (
        <Badge variant="warning" className={className} data-testid="freshness-badge-due-soon">
          {withIcon && <Clock className="h-3 w-3 mr-1" aria-hidden />}
          {t('Due soon')}
        </Badge>
      );
    case 'never':
      return (
        <Badge variant="warning" className={className} data-testid="freshness-badge-never">
          {withIcon && <Clock className="h-3 w-3 mr-1" aria-hidden />}
          {t('Never reviewed')}
        </Badge>
      );
    case 'fresh':
    default:
      return (
        <Badge variant="success" className={className} data-testid="freshness-badge-fresh">
          {withIcon && <CircleCheck className="h-3 w-3 mr-1" aria-hidden />}
          {t('Up to date')}
        </Badge>
      );
  }
}
